import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Badge } from '../ui/badge';
import { Loader2, Truck, Plus, Trash2, Search } from 'lucide-react';
import purchaseService from '../../services/purchaseService';
import productService from '../../services/productService';
import subcontractService from '../../services/subcontractService';

function ManualCreateSubcontractDialog({ open, onOpenChange, onSuccess }) {
  const [suppliers, setSuppliers] = useState([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    supplier_id: '',
    supplier_name: '',
    step_name: '',
    expected_return_date: '',
    notes: '',
    subcontract_order_id: ''
  });

  const [items, setItems] = useState([]);

  // Product search modal state
  const [productSearchOpen, setProductSearchOpen] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [productSearchResults, setProductSearchResults] = useState([]);
  const [productSearchLoading, setProductSearchLoading] = useState(false);
  const [selectedItemIndex, setSelectedItemIndex] = useState(null);

  useEffect(() => {
    if (open) {
      setFormData({
        supplier_id: '',
        supplier_name: '',
        step_name: '',
        expected_return_date: '',
        notes: '',
        subcontract_order_id: ''
      });
      setItems([]);
      setError(null);
      loadSuppliers();
    }
  }, [open]);

  const loadSuppliers = async () => {
    try {
      setLoadingSuppliers(true);
      const result = await purchaseService.getSuppliers(1, 100);
      const supplierList = Array.isArray(result) ? result : (result?.data?.items || result?.items || []);
      setSuppliers(supplierList);
    } catch (err) {
      console.error('Failed to load suppliers:', err);
      setSuppliers([]);
    } finally {
      setLoadingSuppliers(false);
    }
  };

  const handleSupplierChange = (value) => {
    const supplier = suppliers.find(s => s._id === value || s.supplier_id === value);
    setFormData(prev => ({
      ...prev,
      supplier_id: value,
      supplier_name: supplier?.supplier_name || supplier?.name || ''
    }));
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addItem = () => {
    setItems(prev => [
      ...prev,
      {
        item_id: `manual-${Date.now()}`,
        product_id: '',
        product_code: '',
        product_name: '',
        planned_quantity: 1,
        unit_price: 0,
        notes: ''
      }
    ]);
  };

  const removeItem = (index) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const updateItem = (index, field, value) => {
    setItems(prev => prev.map((item, i) => {
      if (i !== index) return item;
      return { ...item, [field]: value };
    }));
  };

  const openProductSearch = (index) => {
    setSelectedItemIndex(index);
    setProductSearchTerm('');
    setProductSearchResults([]);
    setProductSearchOpen(true);
  };

  const searchProducts = useCallback(async () => {
    if (!productSearchTerm.trim()) {
      setProductSearchResults([]);
      return;
    }
    try {
      setProductSearchLoading(true);
      const response = await productService.searchProducts(productSearchTerm, 1, 20);
      setProductSearchResults(response.products || []);
    } catch (err) {
      console.error('Failed to search products:', err);
      setProductSearchResults([]);
    } finally {
      setProductSearchLoading(false);
    }
  }, [productSearchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (productSearchOpen && productSearchTerm) {
        searchProducts();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [productSearchTerm, productSearchOpen, searchProducts]);

  const handleSelectProduct = (product) => {
    if (selectedItemIndex === null) return;
    setItems(prev => prev.map((item, i) => {
      if (i !== selectedItemIndex) return item;
      return {
        ...item,
        product_id: product._id,
        product_code: product.product_id || product.product_code,
        product_name: product.name
      };
    }));
    setProductSearchOpen(false);
    setSelectedItemIndex(null);
  };

  const totalAmount = items.reduce((sum, item) => {
    return sum + (parseFloat(item.planned_quantity) || 0) * (parseFloat(item.unit_price) || 0);
  }, 0);

  const totalQuantity = items.reduce((sum, item) => {
    return sum + (parseFloat(item.planned_quantity) || 0);
  }, 0);

  const handleSubmit = async () => {
    if (!formData.supplier_id) {
      setError('请选择供应商');
      return;
    }
    if (items.length === 0) {
      setError('请至少添加一个外协产品');
      return;
    }
    const invalidItem = items.find(item => !item.product_code);
    if (invalidItem) {
      setError('请为所有行选择产品');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const payloadItems = items.map(item => ({
        product_id: item.product_id,
        product_code: item.product_code,
        product_name: item.product_name,
        planned_quantity: parseFloat(item.planned_quantity) || 0,
        unit_price: parseFloat(item.unit_price) || 0,
        notes: item.notes
      }));

      const data = {
        is_manual: true,
        subcontract_order_id: formData.subcontract_order_id.trim(),
        supplier_id: formData.supplier_id,
        supplier_name: formData.supplier_name,
        step_name: formData.step_name,
        items: payloadItems,
        expected_return_date: formData.expected_return_date,
        notes: formData.notes
      };

      const result = await subcontractService.createSubcontractOrder(data);

      if (onSuccess) {
        onSuccess(result);
      }
      onOpenChange(false);
    } catch (err) {
      console.error('Failed to create manual subcontract:', err);
      setError(err.response?.data?.error || err.message || '创建手动外协单失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5 text-amber-600" />
            手动创建外协单
          </DialogTitle>
          <DialogDescription>
            不关联工单，直接创建外协加工单
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {error && (
            <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="manual-sc-order-id">外协单号</Label>
            <Input
              id="manual-sc-order-id"
              value={formData.subcontract_order_id}
              onChange={(e) => handleChange('subcontract_order_id', e.target.value)}
              placeholder="留空则自动生成（如 SC-20260828-001）"
              disabled={submitting}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="manual-sc-supplier">供应商 <span className="text-red-500">*</span></Label>
              <Select
                value={formData.supplier_id}
                onValueChange={handleSupplierChange}
                disabled={loadingSuppliers || submitting}
              >
                <SelectTrigger id="manual-sc-supplier">
                  <SelectValue placeholder={loadingSuppliers ? '加载中...' : '选择供应商'} />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.map(supplier => (
                    <SelectItem
                      key={supplier._id || supplier.supplier_id}
                      value={supplier._id || supplier.supplier_id}
                    >
                      {supplier.supplier_name || supplier.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="manual-sc-step">工序</Label>
              <Input
                id="manual-sc-step"
                value={formData.step_name}
                onChange={(e) => handleChange('step_name', e.target.value)}
                placeholder="如：热处理、精磨（可选）"
                disabled={submitting}
              />
            </div>
          </div>

          {/* Items Table */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-base font-medium">外协产品</Label>
              <Badge variant="outline">{items.length} 行</Badge>
            </div>

            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>产品编码</TableHead>
                    <TableHead>产品名称</TableHead>
                    <TableHead className="text-center">数量</TableHead>
                    <TableHead className="text-right">单价</TableHead>
                    <TableHead className="text-center">操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground py-4">
                        请添加外协产品
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((item, index) => (
                      <TableRow key={item.item_id}>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openProductSearch(index)}
                            disabled={submitting}
                            className="w-full justify-start"
                          >
                            {item.product_code || '选择产品'}
                          </Button>
                        </TableCell>
                        <TableCell>
                          {item.product_name || '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.planned_quantity}
                            onChange={(e) => updateItem(index, 'planned_quantity', e.target.value)}
                            className="h-8 w-24 mx-auto text-center"
                            disabled={submitting}
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.unit_price}
                            onChange={(e) => updateItem(index, 'unit_price', e.target.value)}
                            className="h-8 w-24 ml-auto text-right"
                            disabled={submitting}
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeItem(index)}
                            disabled={submitting}
                            className="h-8 w-8 text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            <Button
              variant="outline"
              onClick={addItem}
              disabled={submitting}
              className="w-full gap-2"
            >
              <Plus className="h-4 w-4" />
              添加产品
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="manual-sc-return-date">计划交期</Label>
              <Input
                id="manual-sc-return-date"
                type="date"
                value={formData.expected_return_date}
                onChange={(e) => handleChange('expected_return_date', e.target.value)}
                disabled={submitting}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="manual-sc-notes">备注</Label>
              <Input
                id="manual-sc-notes"
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder="外协备注..."
                disabled={submitting}
              />
            </div>
          </div>

          {items.length > 0 && (
            <div className="text-sm text-muted-foreground bg-gray-50 p-2 rounded">
              共 {totalQuantity} 件，预计金额: {totalAmount.toFixed(2)} 元
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            取消
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !formData.supplier_id || items.length === 0}
            className="bg-amber-600 hover:bg-amber-700"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                创建中...
              </>
            ) : (
              <>创建外协单</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Product Search Dialog */}
      <Dialog open={productSearchOpen} onOpenChange={setProductSearchOpen}>
        <DialogContent className="max-w-2xl z-[100]">
          <DialogHeader>
            <DialogTitle>选择产品</DialogTitle>
            <DialogDescription>
              从产品目录中搜索并选择外协产品。
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="按产品编号或名称搜索..."
                value={productSearchTerm}
                onChange={(e) => setProductSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            <div className="max-h-64 overflow-y-auto">
              {productSearchLoading ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : productSearchResults.length === 0 ? (
                <div className="text-center py-4 text-muted-foreground">
                  {productSearchTerm ? '未找到产品。' : '输入搜索词查找产品。'}
                </div>
              ) : (
                <div className="border rounded-md">
                  <div className="grid grid-cols-12 gap-2 p-3 bg-muted/50 font-medium text-sm border-b">
                    <div className="col-span-4">产品编号</div>
                    <div className="col-span-6">名称</div>
                    <div className="col-span-2">单位</div>
                  </div>
                  <div className="divide-y max-h-48 overflow-y-auto">
                    {productSearchResults.map((product) => (
                      <div
                        key={product._id}
                        className="grid grid-cols-12 gap-2 p-3 cursor-pointer hover:bg-muted"
                        onClick={() => handleSelectProduct(product)}
                      >
                        <div className="col-span-4 font-mono text-sm">{product.product_id || product.product_code}</div>
                        <div className="col-span-6">{product.name}</div>
                        <div className="col-span-2 text-sm text-muted-foreground">{product.unit || '-'}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setProductSearchOpen(false)}>
              取消
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

export default ManualCreateSubcontractDialog;
